import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { DfMappingChangesService } from './df-mapping-changes.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-df-mapping-changes',
    templateUrl: './df-mapping-changes.component.html',
    styleUrls: ['./df-mapping-changes.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DfMappingChangesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  rowData: any = {};
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  baseGlobalParams: any = {};
  filterParams: any = {};
  USER_SETTINGS: any = {};

  ssoId: string = '';
  scenarioCode: string = '';
  isIssueKeyEnabled: string = '';
  issueKey: number = 0;

  form!: UntypedFormGroup;
  formData: any;
  loader: boolean = false;
  minimise: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private dfMappingChangesService: DfMappingChangesService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.form = this.fb.group({
      TAX_YEAR: ['', Validators.required],
      IDENTIFIER: ['', Validators.required],
      ITEM_GROUP: ['', Validators.maxLength(22)],
      ITEM_GROUP_DESCRIPTION: ['', Validators.maxLength(250)],
      ITEM_SUB_CODE: ['', Validators.maxLength(22)],
      ITEM_CODE: ['', Validators.maxLength(22)],
      ITEM_DESCRIPTION: ['', Validators.maxLength(250)],
      LINE_GROUPING: ['', Validators.maxLength(22)],
      LINE_NUMBER: ['', Validators.maxLength(22)]
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.populateScreen();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.populateScreen();
    }));
    this.populateScreen();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));

    this.ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    this.scenarioCode = this.baseGlobalParams?.scenarioCode || '';
    this.isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled
      || this.baseGlobalParams?.is_issue_key_enabled
      || 'N';
    this.issueKey = Number(this.baseGlobalParams?.issue_key || this.filterParams?.issue_key || 0);
  }

  populateScreen(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.form.patchValue({
      TAX_YEAR: this.rowData.TAX_YEAR,
      IDENTIFIER: this.rowData.IDENTIFIER,
      ITEM_GROUP: this.rowData.ITEM_GROUP,
      ITEM_GROUP_DESCRIPTION: this.rowData.ITEM_GROUP_DESCRIPTION,
      ITEM_SUB_CODE: this.rowData.ITEM_SUB_CODE,
      ITEM_CODE: this.rowData.ITEM_CODE,
      ITEM_DESCRIPTION: this.rowData.ITEM_DESCRIPTION,
      LINE_GROUPING: this.rowData.LINE_GROUPING,
      LINE_NUMBER: this.rowData.LINE_NUMBER
    });
  }

  save(): void {
    if (this.form.status === 'INVALID') {
      this.gtwSnackbarService.openSnackBar("Please check form details and try again", 'gtw-snackbar--red');
      return;
    }
    const formValues = this.form.value;
    formValues.UPDATED_ON = new Date();
    formValues.UPDATED_BY = this.ssoId;
    const params: saveParams = {
      tax_year: this.baseGlobalParams?.tax_year,
      scenario: this.baseGlobalParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key
    };
    const clob_settings: saveParams = { tax_year: params.tax_year, scenario: params.scenario, jcd_key: params.jcd_key };
    const clob_data = [formValues];
    this.dfMappingChangesService.saveLineChanges(this.baseURLs.api, params, clob_settings, clob_data, this.scenarioCode, this.isIssueKeyEnabled, this.issueKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data !== 'error') {
        this.requestGridUpdate.emit('refresh');
        this.loader = false;
      } else {
        console.log("Error while saving");
        this.loader = false;
      }
    }, () => {
      console.log("Error while saving");
      this.loader = false;
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

export type saveParams = {
  tax_year: string | undefined;
  scenario: string | undefined;
  jcd_key: string | undefined;
}
